import { useState } from "react";
import { Link } from "react-router-dom";
import { BuyerRequirement } from "@/mocks/buyerRequirements";

interface Props {
  req: BuyerRequirement;
  isFactoryMember: boolean;
  onSendQuote: (req: BuyerRequirement) => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  Electronics: "bg-orange-50 text-orange-600",
  Beauty: "bg-rose-50 text-rose-600",
  Sports: "bg-green-50 text-green-600",
  Home: "bg-amber-50 text-amber-600",
  Fashion: "bg-indigo-50 text-indigo-600",
  "Food & Beverage": "bg-lime-50 text-lime-600",
};

export default function RequirementCard({ req, isFactoryMember, onSendQuote }: Props) {
  const [expanded, setExpanded] = useState(false);
  const colorClass = CATEGORY_COLORS[req.category] || "bg-gray-50 text-gray-600";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 hover:border-orange-200 transition-all p-5 flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${colorClass}`}>{req.category}</span>
            <span className="text-xs text-gray-500">{req.countryFlag} {req.country}</span>
            <span className="text-xs text-gray-400">· {req.postedAt}</span>
          </div>
          <h3 className="text-sm font-bold text-gray-900 leading-snug">{req.title}</h3>
        </div>
      </div>

      {/* Key Info */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-gray-50 rounded-xl p-2.5 text-center">
          <p className="text-xs font-bold text-gray-900 truncate">{req.quantity}</p>
          <p className="text-xs text-gray-400 mt-0.5">Quantity</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-2.5 text-center">
          <p className="text-xs font-bold text-gray-900 truncate">{req.budget || "Negotiable"}</p>
          <p className="text-xs text-gray-400 mt-0.5">Budget</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-2.5 text-center">
          <p className="text-xs font-bold text-gray-900 truncate">{req.targetDelivery || "Flexible"}</p>
          <p className="text-xs text-gray-400 mt-0.5">Delivery</p>
        </div>
      </div>

      {/* Description */}
      <p className={`text-xs text-gray-600 leading-relaxed mb-2 ${expanded ? "" : "line-clamp-2"}`}>
        {req.description}
      </p>
      {req.description.length > 120 && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-orange-500 hover:underline cursor-pointer mb-3 text-left">
          {expanded ? "Show less" : "Read more"}
        </button>
      )}

      <div className="flex-1" />

      {/* Contact Info — gated */}
      {isFactoryMember ? (
        <div className="border border-green-100 bg-green-50 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-shield-check-fill text-green-500 text-sm" />
            <span className="text-xs font-bold text-green-700">Verified Factory Access — Buyer Contact</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <i className="ri-user-line text-gray-400 text-sm" />
              <span className="text-xs font-semibold text-gray-800">{req.buyerName}</span>
              {req.company && <span className="text-xs text-gray-400">· {req.company}</span>}
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-mail-line text-orange-500 text-sm" />
              <a href={`mailto:${req.email}`} className="text-xs text-orange-600 hover:underline cursor-pointer">{req.email}</a>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-whatsapp-line text-green-500 text-sm" />
              <a href={`https://wa.me/${req.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="nofollow noreferrer" className="text-xs text-green-600 hover:underline cursor-pointer">{req.whatsapp}</a>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <a href={`mailto:${req.email}`} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-orange-500 text-white text-xs font-bold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer">
              <i className="ri-mail-send-line" /> Email
            </a>
            <a href={`https://wa.me/${req.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="nofollow noreferrer" className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-500 text-white text-xs font-bold rounded-lg hover:bg-green-600 transition-colors cursor-pointer">
              <i className="ri-whatsapp-line" /> WhatsApp
            </a>
          </div>
        </div>
      ) : (
        <div className="border border-dashed border-gray-300 bg-gray-50 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full border border-gray-200 flex-shrink-0">
              <i className="ri-lock-line text-gray-400 text-base" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-gray-700">Contact Info Hidden</p>
              <p className="text-xs text-gray-400 mt-0.5">Buyer name, email &amp; WhatsApp are locked. Upgrade to Factory Membership to unlock.</p>
            </div>
          </div>
          {/* Blurred preview */}
          <div className="mt-3 space-y-1.5 select-none">
            <div className="flex items-center gap-2">
              <i className="ri-user-line text-gray-300 text-sm" />
              <span className="text-xs text-gray-300 blur-sm">James Whitfield · UKTech Ltd.</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-mail-line text-gray-300 text-sm" />
              <span className="text-xs text-gray-300 blur-sm">james@uktech.co.uk</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-whatsapp-line text-gray-300 text-sm" />
              <span className="text-xs text-gray-300 blur-sm">+44 7911 xxxxxx</span>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      {isFactoryMember ? (
        <button
          onClick={() => onSendQuote(req)}
          className="w-full py-2.5 bg-orange-500 text-white text-xs font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
        >
          <i className="ri-send-plane-line" /> Send Quotation to Buyer
        </button>
      ) : (
        <Link
          to="/membership"
          className="w-full py-2.5 border-2 border-orange-400 text-orange-500 text-xs font-bold rounded-full hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
        >
          <i className="ri-vip-crown-line" /> Upgrade to Unlock Contact &amp; Send Quote
        </Link>
      )}
    </div>
  );
}
